import { Popover } from "antd";
import { LoadingIcon } from "components/Icons";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import { useGlobalSettingsContext } from "contexts/GlobalSettingsContext";
import useResolvedFindingStatus from "hooks/useResolvedFindingStatus";
import { compact, find, isEmpty, isNil, isNumber, isUndefined } from "lodash";
import React, { useEffect, useState } from "react";
import GlobalSettingAPI from "restAPI/GlobalSettingAPI";
import { RISK_SCORE_DOC_LINK } from "utils/riskScoreUtil";
import { CellRenderers } from "utils/tableUtils";



import { FINDING_SCORE_SEVERITY_WEIGHT, SSDLC_WEIGHT } from "../Constants";
import { getVocabulary } from "components/Vocabulary";


export default function FindingScoreFormula({ findingScore, severity, cves, aatiScore, additionalDetails, cvssBaseScore, toolName, status}) {
  const [ssdlcTools, setSsdlcTools] = useState();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const featureFlags = useFeatureFlag();
  const resolvedStatus = useResolvedFindingStatus();
  const globalSettings = useGlobalSettingsContext();
  const cveRiskCalculationType = globalSettings?.cveRiskCalculationType;
  const epssProbability = additionalDetails?.epssProbability;
  
  useEffect(() => {
    if(!isPopoverOpen) return;
    const { cancel, promise } = GlobalSettingAPI.getCustomizedTool();
    promise.then(setSsdlcTools);
    return cancel;
  }, [findingScore, isPopoverOpen]);

  let scoreValue = "";
  if (featureFlags.AATI_SCORE_COLUMN){
    if (!isEmpty(compact(cves))) {
      if (cveRiskCalculationType === "AATI_CTI_BASED") {
        if (isNumber(additionalDetails?.ctiScore)) {
          scoreValue = (
            <>
              CTI Score(<strong>{additionalDetails.ctiScore}</strong>)
            </>
          );
        } else if (isNumber(aatiScore) && featureFlags.AATI_SCORE_COLUMN) {
          scoreValue = (
            <>
              AATI Score(<strong>{aatiScore}</strong>)
            </>
          );
        } else if (isNumber(cvssBaseScore)) {
            if (additionalDetails?.exploited) {
              scoreValue = (
                <>
                  NVD Base Score(<strong>{cvssBaseScore}</strong>)
                </>
              );
            } else {
              scoreValue = (
                <>
                  NVD Base Score(<strong>{cvssBaseScore}</strong>) × 0.8
                </>
              );
            }
        }
      } else if (cveRiskCalculationType === "EPSS_CISA_KEV_BASED") {
        if (additionalDetails?.isCISAKEV) {
          if (isNumber(cvssBaseScore)) {
            scoreValue = (
              <>
                NVD Base Score(<strong>{cvssBaseScore}</strong>)
              </>
            );
          }
        } else if (!additionalDetails?.isCISAKEV) {
          if (isNil(epssProbability)) {
            if (isNumber(additionalDetails?.ctiScore)) {
              scoreValue = (
                <>
                  CTI Score(<strong>{additionalDetails.ctiScore}</strong>)
                </>
              );
            } else if (isNumber(aatiScore) && featureFlags.AATI_SCORE_COLUMN) {
              scoreValue = (
                <>
                  AATI Score(<strong>{aatiScore}</strong>)
                </>
              );
            } else if (isNumber(cvssBaseScore)) {
              if (additionalDetails?.exploited) {
                scoreValue = (
                  <>
                    NVD Base Score(<strong>{cvssBaseScore}</strong>)
                  </>
                );
              } else {
                scoreValue = (
                  <>
                    NVD Base Score(<strong>{cvssBaseScore}</strong>) × 0.8
                  </>
                );
              }
          }
          } else {
            if (isNumber(cvssBaseScore)) {
              scoreValue = (
                <>
                  NVD Base Score(<strong>{cvssBaseScore}</strong>) × 
                  <br/>
                  ( 0.8 + (EPSS Score(<strong>{epssProbability}</strong>) × 0.2 )) 
                </>
              );
            }
          }
        }
      }
    } else if (isNumber(cvssBaseScore)) {
        if (additionalDetails?.exploited) {
          scoreValue = (
            <>
              NVD Base Score(<strong>{cvssBaseScore}</strong>)
            </>
          );
        } else {
          scoreValue = (
            <>
              NVD Base Score(<strong>{cvssBaseScore}</strong>) × 0.8
            </>
          );
        }
    }
  } else {
    if (!isEmpty(compact(cves))) {
      if (isNumber(additionalDetails?.ctiScore)) {
        scoreValue = (
          <>
            CTI Score(<strong>{additionalDetails.ctiScore}</strong>)
          </>
        );
      } else if (isNumber(aatiScore) && featureFlags.AATI_SCORE_COLUMN) {
        scoreValue = (
          <>
            AATI Score(<strong>{aatiScore}</strong>)
          </>
        );
      } else if (isNumber(cvssBaseScore)) {
        if (additionalDetails?.isCISAKEV) {
          scoreValue = (
            <>
              Base Score(<strong>{cvssBaseScore}</strong>)
            </>
          );
        } else {
          scoreValue = (
            <>
              Base Score(<strong>{cvssBaseScore}</strong>) × 0.8
            </>
          );
        }
      }
    } else if (isNumber(cvssBaseScore)) {
      if (additionalDetails?.exploited) {
        scoreValue = (
          <>
            Base Score(<strong>{cvssBaseScore}</strong>)
          </>
        );
      } else {
        scoreValue = (
          <>
            Base Score(<strong>{cvssBaseScore}</strong>) × 0.8
          </>
        );
      }
    }
  }
  if (isEmpty(scoreValue)) {
    const toolInfo = find(ssdlcTools, { toolName: toolName, toolTypeString: 'All' }) ||
                find(ssdlcTools, tool => 
                   tool.toolName === toolName && 
                   tool.toolTypeString?.includes(additionalDetails?.scanType)
                 );
    if (toolInfo && toolInfo.weightage) {
      scoreValue = (
        <>
          SSDLC Tool Weight(<strong>{SSDLC_WEIGHT[toolInfo.weightage]}</strong>)
        </>
      );
    } else {
      scoreValue = <>Default Tool Weight(<strong>8</strong>)</>
    }
  }
  const content = isUndefined(ssdlcTools) ? (
    <LoadingIcon />
  ) : (
    <div>
      <strong>{findingScore}</strong> = {scoreValue} × 
      <div>
      {getVocabulary(severity)}{" "}severity value(<strong>{FINDING_SCORE_SEVERITY_WEIGHT[severity]}</strong>)
      </div>
      <div className="p-t-xs text-right tag-tooltip-doc-link">{RISK_SCORE_DOC_LINK}</div>
    </div>
  );

  // If the finding is resolved i.e Mitigated, remediated, false+ve, accept risk or severity is INFO, we should not show any calculation logic tooltip, the scores may not match with the current state of the finding
  const isStatusResolvedOrInfoSeverity = resolvedStatus.includes(status) || severity === "INFO";

  if (isNil(findingScore) || isStatusResolvedOrInfoSeverity || !featureFlags.RISK_SCORE_3_0) {
    return CellRenderers.VALUE_OR_NA(findingScore);
  }
  return (
    <Popover align={{ offset: [0, 6]}} content={content} onVisibleChange={()=>setIsPopoverOpen(prevVal => !prevVal)}>
      <div style={{width: "max-content"}}>{findingScore}</div>
    </Popover>
  );
}